/**
 * Inventory API Client
 *
 * All inventory endpoints are scoped to a farm via /api/v1/farms/:farmId/inventory/*.
 * Templates are global (no farm ID required).
 */

import { api } from './api';
import type { ApiResponse, ApiListResponse } from '@/types/api';
import type {
  InventoryTemplate,
  InventoryItem,
  StockOverview,
  LowStockItem,
  InventoryTransaction,
  CreateItemRequest,
  UpdateItemRequest,
  ReceiveStockRequest,
  AdjustStockRequest,
  ImportTemplatesRequest,
  ImportTemplatesResult,
} from '@/types/inventory';

export const inventoryApi = {
  // ===== Templates (global) =====

  listTemplates: (params?: { category?: string; search?: string }) =>
    api.get<ApiListResponse<InventoryTemplate>>('/api/v1/inventory/templates', {
      params: params as Record<string, string>,
    }),

  // ===== Items (farm-scoped) =====

  listItems: (
    farmId: string,
    params?: {
      category?: string;
      is_active?: string;
      search?: string;
      include_stock?: string;
      limit?: string;
    }
  ) =>
    api.get<ApiListResponse<InventoryItem>>(
      `/api/v1/farms/${farmId}/inventory/items`,
      { params: params as Record<string, string> }
    ),

  getItem: (farmId: string, itemId: string) =>
    api.get<ApiResponse<InventoryItem>>(
      `/api/v1/farms/${farmId}/inventory/items/${itemId}`
    ),

  createItem: (farmId: string, data: CreateItemRequest) =>
    api.post<ApiResponse<InventoryItem>>(
      `/api/v1/farms/${farmId}/inventory/items`,
      data
    ),

  importTemplates: (farmId: string, data: ImportTemplatesRequest) =>
    api.post<ApiResponse<ImportTemplatesResult>>(
      `/api/v1/farms/${farmId}/inventory/items/import`,
      data
    ),

  updateItem: (farmId: string, itemId: string, data: UpdateItemRequest) =>
    api.patch<ApiResponse<InventoryItem>>(
      `/api/v1/farms/${farmId}/inventory/items/${itemId}`,
      data
    ),

  deleteItem: (farmId: string, itemId: string) =>
    api.delete(`/api/v1/farms/${farmId}/inventory/items/${itemId}`),

  pullTemplate: (farmId: string, itemId: string) =>
    api.post<ApiResponse<{ updated_fields: string[]; item: InventoryItem }>>(
      `/api/v1/farms/${farmId}/inventory/items/${itemId}/pull-template`
    ),

  // ===== Stock (farm-scoped) =====

  getStock: (
    farmId: string,
    params?: { category?: string; status?: string; sort_by?: string }
  ) =>
    api.get<ApiResponse<StockOverview>>(
      `/api/v1/farms/${farmId}/inventory/stock`,
      { params: params as Record<string, string> }
    ),

  getLowStock: (farmId: string) =>
    api.get<ApiListResponse<LowStockItem>>(
      `/api/v1/farms/${farmId}/inventory/stock/low`
    ),

  receiveStock: (farmId: string, data: ReceiveStockRequest) =>
    api.post<ApiResponse<InventoryTransaction>>(
      `/api/v1/farms/${farmId}/inventory/receive`,
      data
    ),

  adjustStock: (farmId: string, data: AdjustStockRequest) =>
    api.post<ApiResponse<InventoryTransaction>>(
      `/api/v1/farms/${farmId}/inventory/adjust`,
      data
    ),

  // ===== Transactions (farm-scoped) =====

  listTransactions: (farmId: string, params?: Record<string, string>) =>
    api.get<ApiListResponse<InventoryTransaction>>(
      `/api/v1/farms/${farmId}/inventory/transactions`,
      { params }
    ),

  // ===== Reports (farm-scoped) =====

  getConsumption: (farmId: string, params?: Record<string, string>) =>
    api.get<ApiListResponse<Record<string, unknown>>>(
      `/api/v1/farms/${farmId}/inventory/consumption`,
      { params }
    ),
};
